// ###### IMPORTS
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  saveBalanceSnapshotWithItems,
  deleteBalanceSnapshot,
} from '../../viewServices/assets_api';
import {
  emptyRow,
  rowFromItem,
  toYMD,
  buildItemsPayloadFromRows,
  formatApiError,
  marketSourceToString,
  fmtNum,
} from '../../viewServices/snapshot_form_shared';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import DatePicker from 'shared_items/DatePicker';
import { formatDate } from 'shared_items/formatConventions';
import SnapshotEditableItemsTable from './SnapshotEditableItemsTable';

// ###### COMPONENT — self-contained edit snapshot (same role as ModalAssetEdit)
const ModalSnapshotEdit = ({
  selectedSnapshot,
  items = [],
  onClose,
  onSuccess,
  assets = [],
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [header, setHeader] = useState({
    snapshot_date: '',
    is_closing: true,
    notes: '',
  });
  const [rows, setRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const openedForKeyRef = useRef(null);

  const applySnapshotToForm = useCallback(() => {
    if (!selectedSnapshot) return;
    setHeader({
      snapshot_date: toYMD(selectedSnapshot.snapshot_date),
      is_closing: !!selectedSnapshot.is_closing,
      notes: selectedSnapshot.notes || '',
    });
    const list = items || [];
    setRows(list.length ? list.map(rowFromItem) : [emptyRow()]);
  }, [selectedSnapshot, items]);

  useEffect(() => {
    if (!selectedSnapshot) {
      openedForKeyRef.current = null;
      return;
    }
    const itemSig = `${(items || []).length}:${[...(items || []).map((i) => i.id)].sort((a, b) => a - b).join(',')}`;
    const key = `id:${selectedSnapshot.id}:${itemSig}`;
    if (openedForKeyRef.current === key) return;
    openedForKeyRef.current = key;
    applySnapshotToForm();
    setIsEditMode(false);
  }, [selectedSnapshot, items, applySnapshotToForm]);

  const sortedViewItems = React.useMemo(() => {
    const list = [...(items || [])];
    list.sort((a, b) => {
      const ca = a.asset_category_code || '';
      const cb = b.asset_category_code || '';
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.asset_code || '').localeCompare(b.asset_code || '');
    });
    return list;
  }, [items]);

  const resetAndClose = useCallback(() => {
    openedForKeyRef.current = null;
    setShowDeleteConfirm(false);
    onClose?.();
  }, [onClose]);

  const handleEditToggle = () => {
    applySnapshotToForm();
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    applySnapshotToForm();
    setIsEditMode(false);
  };

  const handleHeaderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHeader((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemsPayload = buildItemsPayloadFromRows(rows, assets);
    if (!itemsPayload) return;

    try {
      setSubmitting(true);
      await saveBalanceSnapshotWithItems({
        id: selectedSnapshot.id,
        is_closing: !!header.is_closing,
        notes: header.notes?.trim() || '',
        items: itemsPayload,
      });
      toast.success('Snapshot updated.');
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to save.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSnapshot = async () => {
    if (!selectedSnapshot?.id) return;
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await deleteBalanceSnapshot(selectedSnapshot.id);
      toast.success('Snapshot deleted.');
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  if (!selectedSnapshot) return null;

  const showReadOnly = !isEditMode;
  const headerTitle = isEditMode ? 'Edit snapshot' : 'Snapshot details';

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl relative max-h-[92vh] flex flex-col">
          {submitting && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
              <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                <SpinnerLoad />
              </div>
            </div>
          )}

          <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 pr-2">{headerTitle}</h2>
            <div className="flex items-center gap-1 shrink-0">
              {showReadOnly && (
                <button
                  type="button"
                  onClick={handleEditToggle}
                  disabled={submitting || deleting}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  title="Edit"
                >
                  <PencilSquareIcon className="h-6 w-6" />
                </button>
              )}
              <button
                type="button"
                onClick={resetAndClose}
                disabled={submitting || deleting}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {showReadOnly ? (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="overflow-y-auto px-4 py-3 space-y-4 flex-1 text-sm">
                <div>
                  <span className="block text-xs font-medium text-gray-600 mb-1">Snapshot date</span>
                  <div
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 w-full max-w-md text-xs"
                    aria-label="Snapshot date"
                  >
                    <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span
                      className={
                        selectedSnapshot.snapshot_date ? 'text-gray-800' : 'text-gray-400'
                      }
                    >
                      {selectedSnapshot.snapshot_date ? (
                        <strong>{formatDate(selectedSnapshot.snapshot_date)}</strong>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-medium text-gray-600 mb-1">Closing snapshot</span>
                  <p className="text-gray-900">{selectedSnapshot.is_closing ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="block text-xs font-medium text-gray-600 mb-1">Notes</span>
                  <p className="text-gray-800 whitespace-pre-line">
                    {selectedSnapshot.notes?.trim() ? selectedSnapshot.notes : '—'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Items</h4>
                  {sortedViewItems.length === 0 ? (
                    <p className="text-gray-500 text-xs">No lines in this snapshot.</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                      <table className="min-w-[640px] w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600 uppercase tracking-wide">
                          <tr>
                            <th className="text-left px-2 py-2 min-w-[140px]">Asset</th>
                            <th className="text-left px-2 py-2 min-w-[80px]">Source</th>
                            <th className="text-right px-2 py-2 min-w-[72px]">Value</th>
                            <th className="text-right px-2 py-2 min-w-[48px]">Qty</th>
                            <th className="text-right px-2 py-2 min-w-[72px]">Mkt unit.</th>
                            <th className="text-left px-2 py-2 min-w-[160px]">market_source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedViewItems.map((it) => (
                            <tr
                              key={it.id ?? `${it.asset}-${it.asset_code}`}
                              className="border-t border-gray-100"
                            >
                              <td className="px-2 py-1.5 align-top">
                                <span className="font-medium text-gray-900">
                                  {it.asset_code ?? it.asset ?? '—'}
                                </span>
                                {it.asset_name ? (
                                  <span className="block text-gray-600 mt-0.5">{it.asset_name}</span>
                                ) : null}
                              </td>
                              <td className="px-2 py-1.5 text-gray-800 align-top">
                                {it.snapshot_source || '—'}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums align-top">
                                {fmtNum(it.snapshot_value)}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums align-top">
                                {fmtNum(it.snapshot_quantity, 2)}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums align-top">
                                {fmtNum(it.market_unitary_value)}
                              </td>
                              <td className="px-2 py-1.5 align-top">
                                <pre className="whitespace-pre-wrap font-mono text-[11px] text-gray-700 max-h-24 overflow-y-auto">
                                  {it.market_source != null
                                    ? marketSourceToString(it.market_source)
                                    : '—'}
                                </pre>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="overflow-y-auto px-4 py-3 space-y-4 flex-1">
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Snapshot date</label>
                  <div className="flex flex-wrap items-center gap-4">
                    <DatePicker
                      name="snapshot_date"
                      value={header.snapshot_date}
                      onChange={handleHeaderChange}
                      mode="single"
                      colorScheme="green"
                      disabled
                      placeholder="Select date"
                      className="w-48 justify-between min-h-[2.25rem] shrink-0"
                    />
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        name="is_closing"
                        checked={header.is_closing}
                        onChange={handleHeaderChange}
                        disabled={submitting}
                        className="rounded border-gray-300 text-green-700 focus:ring-green-600"
                      />
                      Closing snapshot
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
                  <textarea
                    name="notes"
                    value={header.notes}
                    onChange={handleHeaderChange}
                    rows={2}
                    disabled={submitting}
                    className="w-full px-2 py-1.5 border rounded-md text-sm resize-y min-h-[2.5rem]"
                    placeholder="Optional"
                  />
                </div>

                <SnapshotEditableItemsTable
                  rows={rows}
                  updateRow={updateRow}
                  addRow={addRow}
                  removeRow={removeRow}
                  assets={assets}
                  submitting={submitting}
                />
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 bg-gray-50 px-4 py-3 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={submitting || deleting}
                  className="shrink-0 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Delete snapshot
                </button>
                <p className="min-w-0 flex-1 text-left text-xs text-gray-500">
                  At least one line with an asset and value is required.
                </p>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={submitting}
                  className="shrink-0 rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`shrink-0 rounded-xl px-5 py-2 text-sm font-medium text-white ${
                    submitting ? 'cursor-not-allowed bg-green-400' : 'bg-green-700 hover:bg-green-800'
                  }`}
                >
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ModalConfirm
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSnapshot}
        title="Delete snapshot"
        message="All items for this snapshot will be removed. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        disabled={deleting}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
      />
    </>
  );
};

export default ModalSnapshotEdit;
